import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useRouteContext } from '@tanstack/react-router';
import { Button, Col, Flex, Form, Input, message, Result, Row, Select, Switch } from 'antd';
import { isAxiosError } from 'axios';
import { ArrowLeft, RotateCw } from 'lucide-react';
import { useEffect } from 'react';

import {
  getGetOrganizationAiSettingsQueryKey,
  useGetOrganizationAiSettings,
  useUpdateOrganizationAiSettings,
} from '../../../../api';
import { usePermissions } from '../../../../modules/account/hooks';
import { SettingsLayoutContent } from '../../../../ui/components/layout';
import { List } from '../../../../ui/components/list';
import { Loader } from '../../../../ui/components/loader';
import { Record } from '../../../../ui/components/record';
import { useUnsavedChangesGuard } from '../../../../ui/hooks';

const SettingsAiBehaviorRoute = () => {
  const queryClient = useQueryClient();

  const { selectedOrganization } = useRouteContext({ from: '/_authenticated/_app' });
  const { can } = usePermissions();
  const organizationId = selectedOrganization.id;
  const canEdit = can('settings', 'manageAiSettings');

  const {
    data: settingsResponse,
    error,
    status,
    refetch,
  } = useGetOrganizationAiSettings(organizationId);
  const settings = settingsResponse?.data;

  const [form] = Form.useForm();
  Form.useWatch([], form);
  useUnsavedChangesGuard(() => form.isFieldsTouched());

  useEffect(() => {
    form.resetFields();
  }, [form, settings]);

  const { mutate: updateSettings, isPending } = useUpdateOrganizationAiSettings({
    mutation: {
      onSuccess: () => {
        message.success('Saved');
        queryClient.invalidateQueries({
          queryKey: getGetOrganizationAiSettingsQueryKey(organizationId),
        });
      },
      onError: () => {
        message.error('Failed to save');
      },
    },
  });

  const content = {
    pending: <Loader />,
    error:
      isAxiosError(error) && error.response?.status === 403 ? (
        <Result
          status="403"
          title="Access Denied"
          subTitle="You do not have permission to view or edit this page. Please contact your administrator."
          extra={
            <Button icon={<ArrowLeft size="1em" />} onClick={() => window.history.back()}>
              Go back
            </Button>
          }
        />
      ) : (
        <Result
          status="error"
          title="Failed to load!"
          subTitle="Please try again. If this keeps happening, check your connection or contact support."
          extra={
            <Button icon={<RotateCw size="1em" />} onClick={() => refetch()}>
              Try again
            </Button>
          }
        />
      ),
    success: (
      <>
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item name="tone" label="Preferred tone">
              <Select
                data-testid="ai-behavior-tone"
                options={[
                  { value: 'formal', label: 'Formal' },
                  { value: 'casual', label: 'Casual' },
                  { value: 'direct', label: 'Direct' },
                  { value: 'friendly', label: 'Friendly' },
                  { value: 'warm', label: 'Warm professional' },
                ]}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="verbosity" label="Message length">
              <Select
                data-testid="ai-behavior-verbosity"
                options={[
                  { value: 'brief', label: 'Brief' },
                  { value: 'short', label: 'Short' },
                  { value: 'moderate', label: 'Moderate' },
                  { value: 'comprehensive', label: 'Comprehensive' },
                ]}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="messageStructure" label="Structure preference">
          <Select
            data-testid="ai-behavior-structure"
            options={[
              { value: 'plain', label: 'Plain text' },
              { value: 'formatted', label: 'Formatted' },
              { value: 'hybrid', label: 'Hybrid' },
            ]}
          />
        </Form.Item>

        <Form.Item name="emphasize" label="What the AI should say / emphasize">
          <Input.TextArea
            placeholder={`- Confirm next steps\n- Ask one clear question\n- Keep it respectful and concise`}
            rows={5}
          />
        </Form.Item>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item name="avoid" label="What the AI should avoid">
              <Input.TextArea
                placeholder={`- No pressure language\n- No guarantees about pricing or delivery\n- Avoid buzzwords`}
                rows={5}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="exclude" label="Words / phrases to exclude">
              <Input.TextArea placeholder={`synergy\nrevolutionary\nbest-in-class`} rows={5} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label="Communication rules" dependencies={[]}>
          {() => (
            <List
              items={[
                {
                  key: 'noFluff',
                  title: 'No fluff',
                  description: 'Avoid filler. Keep messages tight and specific',
                },
                {
                  key: 'requireQuestion',
                  title: 'Ask one question',
                  description: 'Prefer a single clear question at the end',
                },
                {
                  key: 'allowEmojis',
                  title: 'Allow emojis',
                  description: 'Use emojis to make messages more expressive',
                },
                {
                  key: 'requireCta',
                  title: 'Always include a CTA',
                  description: 'End with a clear next step like scheduling or confirming',
                },
              ]}
              renderItem={(item) => ({
                key: item.key,
                reactNode: (
                  <Record
                    title={item.title}
                    description={item.description}
                    right={
                      <Form.Item name={['constraints', item.key]} valuePropName="checked" noStyle>
                        <Switch data-testid={`ai-behavior-${item.key}`} />
                      </Form.Item>
                    }
                  />
                ),
              })}
            />
          )}
        </Form.Item>
      </>
    ),
  };

  return (
    <Form
      form={form}
      layout="vertical"
      disabled={!canEdit}
      onFinish={(values) => {
        updateSettings({
          organizationId,
          data: {
            ...values,
            emphasize: values.emphasize || null,
            avoid: values.avoid || null,
            exclude: values.exclude || null,
          },
        });
      }}
      initialValues={settings}
    >
      <SettingsLayoutContent
        title="AI Behavior"
        description="Configure how the AI behaves in your company."
        footer={
          canEdit ? (
            <Form.Item shouldUpdate noStyle>
              {({ isFieldsTouched }) => (
                <Flex justify={isFieldsTouched() ? 'space-between' : 'flex-end'}>
                  {isFieldsTouched() ? (
                    <Button onClick={() => form.resetFields()} disabled={isPending}>
                      Reset
                    </Button>
                  ) : null}
                  <Button type="primary" htmlType="submit" loading={isPending}>
                    Save
                  </Button>
                </Flex>
              )}
            </Form.Item>
          ) : null
        }
      >
        {content[status]}
      </SettingsLayoutContent>
    </Form>
  );
};

export const Route = createFileRoute('/_authenticated/_app/settings/ai-behavior')({
  component: SettingsAiBehaviorRoute,
});
